import React from 'react';
import { Alert, Box, ExpandableSection, SpaceBetween, StatusIndicator, Table } from '@amzn/awsui-components-react';
import { BlockedItem, BulkPreflightResult } from 'src/models/promotion/BulkPromotionTypes';
import { BulkValidationMode } from 'src/models/promotion/bulkPromotionEnums';
import { getBlockedReasonPresentation, isFailureReason } from './bulkPromotionBands';
import PrerequisiteList from './PrerequisiteList';
import { BulkPromotionContext } from 'src/services/bulk-promotion/BulkPromotionContext';

export interface BlockedReasonGroup {
    reason: string;
    label: string;
    remedy: string;
    items: BlockedItem[];
}

/** Groups blocked items by reason, largest group first. No two reasons are merged. */
export function groupBlockedByReason(blocked: BlockedItem[]): BlockedReasonGroup[] {
    const byReason = new Map<string, BlockedItem[]>();
    for (const item of blocked) {
        const existing = byReason.get(item.reason);
        if (existing) {
            existing.push(item);
        } else {
            byReason.set(item.reason, [item]);
        }
    }
    return Array.from(byReason.entries())
        .map(([reason, items]) => {
            const [label, remedy] = getBlockedReasonPresentation(reason, items[0].detail);
            return { reason, label, remedy, items };
        })
        .sort((a, b) => b.items.length - a.items.length);
}

/**
 * True unless the verdict was produced under PARITY, the only mode that compared a dependency against the
 * source rather than merely finding it present.
 *
 * Allow-list, and it fails CLOSED: an absent or unrecognised mode reads as unverified, because the safe
 * reading of "we do not know what was checked" is "assume nothing was".
 *
 * PARITY is the service's own value — Constants.java VALIDATION_MODE_PARITY, carried on the batch's
 * config_snapshot and surfaced by BulkPromotionConfirmComponent.validationModeOf. Earlier versions of this
 * function compared against FULL and DEGRADED_TARGET_READS_DISABLED, names this client invented and the
 * service never writes, so the warning either never fired or fired on every batch including fully
 * validated ones.
 */
export function isVerdictDependencyUnverified(validationMode?: string): boolean {
    return validationMode !== BulkValidationMode.PARITY;
}

/**
 * The verdict the operator confirms against: what will promote, what is blocked and why, and what
 * has to happen first.
 */
export default function PreflightSummary() {
    const { bulkPromotionState } = React.useContext(BulkPromotionContext);
    const preflight = bulkPromotionState.preflight as BulkPreflightResult;
    if (!preflight) {
        return null;
    }
    /*
     * Every list is defaulted here rather than at each use. None of promotable, blocked, prerequisites or
     * skipped is @required on the Smithy BulkPreflightResult, and the verdict reaches this component from
     * three different constructors — the frame merge, the parked-record rebuild, and the raw wire payload —
     * so an absent list arrives as undefined and throws inside render, which unmounts the tree and leaves
     * the operator a blank page mid-batch.
     */
    const blocked = preflight.blocked || [];
    const promotable = preflight.promotable || [];
    const prerequisites = preflight.prerequisites || [];
    const groups = groupBlockedByReason(blocked);
    const dependencyUnverified = isVerdictDependencyUnverified(preflight.validationMode);
    const skipped = preflight.skipped || [];
    // The summary's count is authoritative; the rows come from frames that can go missing. Showing
    // the row count as the total would under-report the blocked set whenever a frame was lost.
    const blockedTotal = preflight.blockedCount ?? blocked.length;
    const framesMissing =
        preflight.totalPages !== undefined &&
        preflight.pagesReceived !== undefined &&
        preflight.pagesReceived < preflight.totalPages;

    return (
        <SpaceBetween direction="vertical" size="l">
            {dependencyUnverified && (
                <Alert type="warning" header="Dependencies were not fully checked">
                    This verdict was not produced under a full validation, so a dependency may be present in the target
                    without matching the source. Treat it as unverified rather than as a clean pass.
                </Alert>
            )}
            {framesMissing && (
                <Alert type="warning" header="Part of the blocked list did not arrive">
                    {preflight.pagesReceived} of {preflight.totalPages} pages of blocked detail reached this page, so the
                    reasons below are incomplete. The counts are still right. Re-read the batch from its history to see
                    every blocked component.
                </Alert>
            )}
            <Box>
                <Box variant="h3">Verdict</Box>
                <Box variant="p">
                    {promotable.length} of {preflight.acceptedCount} will promote. {blockedTotal} blocked.
                    {skipped.length > 0 && ` ${skipped.length} already up to date.`}
                </Box>
            </Box>
            {promotable.length > 0 && (
                <ExpandableSection headerText={`Will promote (${promotable.length})`}>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {promotable.map((componentId) => (
                            <div key={componentId}>{componentId}</div>
                        ))}
                    </div>
                </ExpandableSection>
            )}
            {skipped.length > 0 && (
                <ExpandableSection
                    headerText={`Already up to date (${skipped.length})`}
                    // Banded success, and stated as a non-failure in the header: this is
                    // UNCHANGED_SINCE_LAST_PROMOTION, the one reason that is not a failure, and the
                    // intended outcome of re-running a converged batch.
                    headerActions={<StatusIndicator type="success">Nothing to do</StatusIndicator>}>
                    <SpaceBetween direction="vertical" size="s">
                        <Box variant="p">
                            These are already identical in the target, so they are not promoted again and they are not
                            failures.
                        </Box>
                        <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                            {skipped.map((componentId) => (
                                <div key={componentId}>{componentId}</div>
                            ))}
                        </div>
                    </SpaceBetween>
                </ExpandableSection>
            )}
            {groups.map((group) => (
                <ExpandableSection
                    key={group.reason}
                    headerText={`${group.label} (${group.items.length})`}
                    // UNCHANGED_SINCE_LAST_PROMOTION can reach this list from a recovered batch
                    // record, so the non-failure rule is applied here too rather than only to the
                    // skipped section above.
                    headerActions={
                        isFailureReason(group.reason) ? (
                            <StatusIndicator type="warning">Blocked</StatusIndicator>
                        ) : (
                            <StatusIndicator type="success">Nothing to do</StatusIndicator>
                        )
                    }
                    defaultExpanded={true}>
                    <SpaceBetween direction="vertical" size="s">
                        <Box variant="p">{group.remedy}</Box>
                        <Table
                            variant="embedded"
                            trackBy="componentId"
                            items={group.items}
                            columnDefinitions={[
                                {
                                    id: 'componentId',
                                    header: 'Component',
                                    cell: (item: BlockedItem) => item.componentId,
                                    isRowHeader: true,
                                },
                                {
                                    id: 'detail',
                                    header: 'Detail',
                                    cell: (item: BlockedItem) => item.detail,
                                },
                                {
                                    id: 'blocker',
                                    header: 'Blocked by',
                                    cell: (item: BlockedItem) => item.missingDependency || '',
                                },
                            ]}
                            empty={<Box textAlign="center">None.</Box>}
                        />
                    </SpaceBetween>
                </ExpandableSection>
            ))}
            <PrerequisiteList prerequisites={prerequisites} />
        </SpaceBetween>
    );
}
